/**
 * @fileoverview Client-side utility functions.
 *
 * This module provides common utility functions used throughout the
 * dashboard frontend. Includes className merging, number/date formatting,
 * and browser utilities.
 *
 * @module client/lib/utils
 */

import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { format, parseISO } from 'date-fns';

// =============================================================================
// Class Name Utilities
// =============================================================================

/**
 * Merges class names with Tailwind CSS conflict resolution.
 *
 * Combines clsx for conditional class joining with tailwind-merge
 * for intelligent handling of Tailwind CSS class conflicts.
 *
 * @param inputs - Class values to merge (strings, objects, arrays, etc.)
 * @returns Merged class name string
 *
 * @example
 * // Basic usage
 * cn('px-4 py-2', 'bg-blue-500'); // 'px-4 py-2 bg-blue-500'
 *
 * @example
 * // Conditional classes
 * cn('btn', { 'btn-primary': isPrimary, 'btn-disabled': isDisabled });
 *
 * @example
 * // Tailwind conflict resolution
 * cn('px-4', 'px-8'); // 'px-8' (later value wins)
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

// =============================================================================
// Number Formatting
// =============================================================================

/**
 * Formats large numbers with K/M suffixes for readability.
 *
 * Used for displaying statistics like click counts in a compact format.
 * Numbers under 1000 are displayed as-is.
 *
 * @param num - Number to format
 * @returns Formatted string with suffix (K for thousands, M for millions)
 *
 * @example
 * formatNumber(500);      // '500'
 * formatNumber(1500);     // '1.5K'
 * formatNumber(1500000);  // '1.5M'
 */
export function formatNumber(num: number): string {
  if (num >= 1_000_000) {
    return `${(num / 1_000_000).toFixed(1)}M`;
  }
  if (num >= 1_000) {
    return `${(num / 1_000).toFixed(1)}K`;
  }
  return num.toString();
}

// =============================================================================
// Date Formatting
// =============================================================================

/**
 * Formats a date in a human-readable format using date-fns.
 *
 * Returns dates in "Mon DD, YYYY" format (e.g., "Dec 3, 2024").
 * Accepts both Date objects and ISO 8601 strings.
 *
 * @param date - Date object or ISO 8601 string
 * @returns Formatted date string
 *
 * @example
 * formatDate(new Date());           // 'Dec 3, 2024'
 * formatDate('2024-01-15T12:00:00Z'); // 'Jan 15, 2024'
 */
export function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(d, 'MMM d, yyyy');
}

/**
 * Formats a date as an ISO date string (yyyy-MM-dd).
 *
 * Commonly used for API requests and date picker values.
 * Uses date-fns format for consistency across timezones.
 *
 * @param date - Date object to format
 * @returns ISO date string in yyyy-MM-dd format
 *
 * @example
 * getDateString(new Date('2024-12-08')); // '2024-12-08'
 */
export function getDateString(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

/**
 * Get UTC date string for API requests.
 *
 * IMPORTANT: Use this instead of getDateString when making API requests
 * to the backend. The backend stores all dates in UTC, so we must send
 * UTC dates to avoid timezone mismatches (e.g., missing today's data
 * when local time differs from UTC).
 *
 * @param date - Date object to format
 * @returns UTC date string in yyyy-MM-dd format
 *
 * @example
 * // At 6 PM PST on Jan 4th (2 AM UTC Jan 5th):
 * getDateString(new Date());    // '2026-01-04' (local - WRONG for API)
 * getUTCDateString(new Date()); // '2026-01-05' (UTC - CORRECT for API)
 */
export function getUTCDateString(date: Date): string {
  // toISOString() always returns 'YYYY-MM-DDTHH:mm:ss.sssZ', format is guaranteed
  return date.toISOString().slice(0, 10);
}

/**
 * Formats a date in a human-readable format with timezone support.
 *
 * Returns dates in "Mon DD, YYYY" format (e.g., "Dec 3, 2024") converted
 * to the specified timezone. If no timezone is provided, uses the browser's timezone.
 *
 * @param date - Date object or ISO 8601 string
 * @param timezone - Optional IANA timezone identifier (e.g., "America/Los_Angeles")
 * @returns Formatted date string
 *
 * @example
 * formatDateWithTimezone('2024-01-15T12:00:00Z', 'America/New_York'); // 'Jan 15, 2024'
 */
export function formatDateWithTimezone(date: Date | string, timezone?: string): string {
  const d = typeof date === 'string' ? parseISO(date) : date;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(d);
  } catch {
    // Fallback to date-fns if timezone is invalid
    return format(d, 'MMM d, yyyy');
  }
}

/**
 * Formats a timestamp as time with timezone support.
 *
 * Returns time in "HH:MM:SS AM/PM" format converted to the specified timezone.
 * If no timezone is provided, uses the browser's timezone.
 *
 * @param timestamp - ISO 8601 timestamp string or Date object
 * @param timezone - Optional IANA timezone identifier (e.g., "America/Los_Angeles")
 * @returns Formatted time string
 *
 * @example
 * formatTimeWithTimezone('2024-01-15T18:30:00Z', 'America/New_York'); // '1:30:00 PM'
 */
export function formatTimeWithTimezone(timestamp: Date | string, timezone?: string): string {
  const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    }).format(d);
  } catch {
    // Fallback to basic formatting if timezone is invalid
    return d.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  }
}

/**
 * Formats a date and time with timezone support.
 *
 * Returns combined date and time string converted to the specified timezone.
 *
 * @param timestamp - ISO 8601 timestamp string or Date object
 * @param timezone - Optional IANA timezone identifier
 * @returns Formatted date and time string
 *
 * @example
 * formatDateTimeWithTimezone('2024-01-15T18:30:00Z', 'America/New_York');
 * // 'Jan 15, 2024 1:30 PM'
 */
export function formatDateTimeWithTimezone(timestamp: Date | string, timezone?: string): string {
  const d = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(d);
  } catch {
    // Fallback to basic formatting
    return `${format(d, 'MMM d, yyyy')} ${d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  }
}

/**
 * Formats a date as relative time (e.g., "5m ago", "2d ago").
 *
 * Shows relative time for recent dates and falls back to absolute
 * date format for older dates (> 1 week).
 *
 * Thresholds:
 * - < 1 minute: "just now"
 * - < 1 hour: "Xm ago"
 * - < 1 day: "Xh ago"
 * - < 1 week: "Xd ago"
 * - >= 1 week: Absolute date (via formatDate)
 *
 * @param date - Date object, ISO 8601 string, or numeric timestamp (ms)
 * @returns Relative time string
 *
 * @example
 * formatRelativeTime(new Date(Date.now() - 30000));    // 'just now'
 * formatRelativeTime(new Date(Date.now() - 300000));   // '5m ago'
 * formatRelativeTime(Date.now() - 7200000);            // '2h ago'
 */
export function formatRelativeTime(date: Date | string | number): string {
  let d: Date;
  if (typeof date === 'number') {
    // Numeric timestamp (milliseconds since epoch)
    d = new Date(date);
  } else if (typeof date === 'string') {
    // Try parseISO first (strict ISO 8601), fall back to native Date constructor
    d = parseISO(date);
    if (isNaN(d.getTime())) {
      d = new Date(date);
    }
  } else {
    d = date;
  }

  // Handle invalid dates gracefully
  if (isNaN(d.getTime())) {
    return 'Unknown';
  }

  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - d.getTime()) / 1000);

  // Less than 1 minute
  if (diffInSeconds < 60) {
    return 'just now';
  }
  // Less than 1 hour
  if (diffInSeconds < 3600) {
    const minutes = Math.floor(diffInSeconds / 60);
    return `${minutes}m ago`;
  }
  // Less than 1 day
  if (diffInSeconds < 86400) {
    const hours = Math.floor(diffInSeconds / 3600);
    return `${hours}h ago`;
  }
  // Less than 1 week
  if (diffInSeconds < 604800) {
    const days = Math.floor(diffInSeconds / 86400);
    return `${days}d ago`;
  }
  // Fall back to absolute date
  return formatDate(d);
}

// =============================================================================
// Browser Utilities
// =============================================================================

/**
 * Copies text to the system clipboard.
 *
 * Uses the modern Clipboard API. Must be called in response to a
 * user interaction (click) due to browser security restrictions.
 *
 * @param text - Text to copy to clipboard
 * @returns Promise that resolves when copy is complete
 * @throws Error if clipboard access is denied
 *
 * @example
 * await copyToClipboard('https://arml.ink/abc123');
 * toast.success('Link copied!');
 */
export function copyToClipboard(text: string): Promise<void> {
  return navigator.clipboard.writeText(text);
}

/**
 * Gets the base URL of the current application.
 *
 * In the browser, returns the current origin (protocol + host).
 * On the server (SSR), falls back to APP_URL environment variable.
 *
 * @returns Base URL string
 *
 * @example
 * getBaseUrl(); // 'https://dashboard.arml.ink' or 'http://localhost:5173'
 */
export function getBaseUrl(): string {
  if (typeof window !== 'undefined') {
    return window.location.origin;
  }
  return process.env.APP_URL || 'http://localhost:5173';
}

// =============================================================================
// String Utilities
// =============================================================================

/**
 * Truncates a URL to a maximum length for display.
 *
 * Commonly used in tables and lists where URLs need to fit in
 * a constrained width. Appends ellipsis (...) when truncated.
 *
 * @param url - The URL string to truncate
 * @param maxLength - Maximum length before truncation (default: 40)
 * @returns Truncated URL with ellipsis if needed, or original URL if short enough
 *
 * @example
 * truncateUrl('https://example.com/very/long/path', 30);
 * // Returns: 'https://example.com/very/lo…'
 *
 * @example
 * truncateUrl('https://short.url', 50);
 * // Returns: 'https://short.url' (no truncation needed)
 */
export function truncateUrl(url: string, maxLength = 40): string {
  if (url.length <= maxLength) return url;
  return `${url.substring(0, maxLength - 1)}…`;
}
